import { DeliveryWindowDateRecord } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { CustomDateFormats, NoddiDateFormats, noddiFormatDate, parseDate } from 'noddi-util';
import { countBookableTimeWindows, getWeekdayLabel, UNAVAILABLE_TEXT, UNAVAILABLE_TEXT_ON_PAGE } from './utils';

type Translations = {
  today: string;
  tomorrow: string;
  full: string;
  availableCount: (count: number) => string;
};

type Props = {
  dateRecord: DeliveryWindowDateRecord;
  translations: Translations;
  /** Renders the header as a phone day tab that shows its windows in `panelId`; without it, a static column head. */
  tab?: {
    isActive: boolean;
    onSelect: () => void;
    panelId: string;
    /** The picker sits straight on the page background, so the tab row has a card of its own. */
    isOnPage: boolean;
  };
};

export const DayHeader = ({ dateRecord, translations, tab }: Props) => {
  const bookableCount = countBookableTimeWindows(dateRecord);
  const lines = (
    <DayHeaderLines
      dateRecord={dateRecord}
      translations={translations}
      bookableCount={bookableCount}
      isActive={tab?.isActive}
      isColumnHead={!tab}
      isOnPage={tab?.isOnPage ?? false}
    />
  );

  if (!tab) {
    return <div className='flex flex-col items-center border-b border-border px-1 pt-2 pb-2.5'>{lines}</div>;
  }

  return (
    <button
      type='button'
      role='tab'
      aria-selected={tab.isActive}
      aria-controls={tab.panelId}
      onClick={tab.onSelect}
      className={cn(
        // Every tab carries the active tab's 3px border, so switching tabs never shifts their content.
        'flex min-w-0 flex-1 flex-col items-center rounded-t-md border-b-3 px-0.5 pt-2 pb-2.5 transition-colors max-[359px]:px-0',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
        tab.isActive
          ? // On the page the tabs have a white card of their own, where the underline alone marks the day.
            cn('border-primary text-primary', !tab.isOnPage && 'bg-card-light-background')
          : 'border-transparent hover:border-primary/20'
      )}
    >
      {lines}
    </button>
  );
};

const DayHeaderLines = ({
  dateRecord,
  translations,
  bookableCount,
  isActive = false,
  isColumnHead,
  isOnPage
}: {
  dateRecord: DeliveryWindowDateRecord;
  translations: Translations;
  bookableCount: number;
  isActive?: boolean;
  isColumnHead: boolean;
  isOnPage: boolean;
}) => {
  const isFull = bookableCount === 0;
  const unavailableText = isOnPage ? UNAVAILABLE_TEXT_ON_PAGE : UNAVAILABLE_TEXT;
  // A calendar date (YYYY-MM-DD) parsed as local midnight: `new Date()` would read it as UTC.
  const day = parseDate(dateRecord.date, NoddiDateFormats.API_DATE);

  // The active tab inherits the primary color from its button; idle days color each line themselves.
  return (
    <>
      <span
        className={cn(
          // Below 360px a fifth of the row is narrower than "I morgen" (with the tab row's own narrow-screen
          // padding and gap), which would spill past the active tab's fill.
          'text-sm whitespace-nowrap max-[359px]:text-xs',
          // On the page, grey reads as a dirty patch on the colored background, so the weekday takes the brand tint.
          isActive ? 'font-medium' : isFull ? unavailableText : isOnPage ? 'text-primary/80' : 'text-muted-foreground'
        )}
      >
        {getWeekdayLabel(day, translations)}
      </span>
      <span
        className={cn(
          'text-xl leading-tight',
          isFull ? 'font-medium' : 'font-semibold',
          isFull && !isActive && unavailableText
        )}
      >
        {noddiFormatDate(day, CustomDateFormats.DAY_OF_MONTH)}
      </span>
      <span
        className={cn(
          'text-xs whitespace-nowrap',
          isFull
            ? !isActive && unavailableText
            : // Availability is secondary to the date; active phone tabs retain their brand emphasis.
              isColumnHead
              ? 'text-muted-foreground'
              : cn('font-medium', !isActive && 'text-primary/80')
        )}
      >
        {isFull ? translations.full : translations.availableCount(bookableCount)}
      </span>
    </>
  );
};
